// Ported from dev-scrips-pm-react src/billing/pages/invoice-detail-page.tsx (PROD-3312 appointment Billing tab).
//
// The invoice body in pm-react's structure and order: header (title, status and payment chips, actions), balance cards,
// cancelled / removed-order / validation banners, the Invoice heading, info grid, diagnosis, services, pending orders,
// note + totals (with the Draft-only pending-orders total), package redemptions, payments. The actions render under
// pm-react's exact conditions and are all wired:
//   - Issue + Cancel dialogs, Add service picker, the Draft line edits (qty, remove, coverage, service date, auth code,
//     validity);
//   - the line-discount pencil and the management-discount dialog;
//   - the Add payment form, receipt Print and Void, and the hand-off to the host's cashier-shift dialog;
//   - pending orders (section, total box) and the order-line Keep / Drop / Revert.
// Print Invoice and receipt Print are wired: the PA's `usePrintInvoice` / `usePrintReceipt` feed the print-preview
// store, and `AppShell` mounts the one PrintPreviewDialog app-wide (pm-react mounts its own here).
//
// Primitives come from the PROD-3310 billing bridge (`@/billing/bridge`), which renders Signal DS underneath with
// pm-react's prop API. pm-react's arbitrary-value classes are the named utilities on the same tokens (the PA forbids
// arbitrary classes): page pad 48 -> `px-12` / `pb-12`, header gap 32 -> `pt-8`, body 13/20 -> `text-sm leading-5`,
// meta 12/16 -> `text-xs leading-4`; the 1fr / 320px note-and-totals grid is the same split in flex (`md:w-80`).
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ChevronLeft, X } from 'lucide-react';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { BridgeButton as Button, BridgeStatusChip, PageHeader, Skeleton } from '@/billing/bridge';
import { useAuthStore } from '@/auth/auth-store';
import { useInvoice } from '@/billing/services/use-invoice';
import { useInvoiceLineEdits } from '@/billing/services/use-invoice-line-edits';
import { usePaymentsForInvoice } from '@/billing/services/use-payments';
import { usePrintInvoice, usePrintReceipt } from '@/billing/services/use-invoice-actions';
import { useInvoiceOrderLines } from '@/billing/services/use-invoice-order-lines';
import { AddPaymentForm } from '@/billing/components/add-payment-form';
import { VoidPaymentDialog } from '@/billing/components/void-payment-dialog';
import { PaymentReceiptCard } from '@/billing/components/payment-receipt-card';
import { PackageRedemptionsNote } from '@/billing/components/package-redemptions-note';
import { InvoiceReceiptActions } from '@/billing/components/invoice-receipt-actions';
import { ClaimReviewInfoGrid } from '@/billing/components/claim-review/info-grid';
import { ClaimReviewDiagnosisSection } from '@/billing/components/claim-review/diagnosis-section';
import { ClaimReviewTotalsBox } from '@/billing/components/claim-review/totals-box';
import { InvoiceServicesTable } from '@/billing/components/invoice-services-table';
import { InvoiceCancelDialog } from '@/billing/components/invoice-cancel-dialog';
import { InvoiceIssueDialog } from '@/billing/components/invoice-issue-dialog';
import { InvoicePendingOrders } from '@/billing/components/invoice-pending-orders';
import { InvoicePendingOrdersTotal } from '@/billing/components/invoice-pending-orders-total';
import { ServicePickerDialog } from '@/billing/components/service-picker-dialog';
import { EditServiceDiscountDialog } from '@/billing/components/edit-service-discount-dialog';
import { ManagementDiscountDialog } from '@/billing/components/management-discount-dialog';
import { isSelfPay as isSelfPayType } from '@/billing/lib/sponsor-type';
import { ISSUE_BLOCKED_HINT, PAYMENT_BADGE_SEMANTIC, STATUS_BADGE_SEMANTIC } from '@/billing/lib/invoice-status';
import { PatientBalanceCards, type PatientBalanceEntry } from '@/billing/components/patient-balance-cards';
import type { InvoiceService, PaymentRow } from '@/shared/types/billing';

export type { PatientBalanceEntry } from '@/billing/components/patient-balance-cards';

interface InvoiceDetailPageProps {
  /** When provided, overrides `useParams().invoiceId` (panel embed). */
  invoiceId?: string;
  /**
   * When true, hide route-only chrome (breadcrumb row, back-arrow
   * chevron, and the ErrorState back button). Title text stays.
   */
  embedded?: boolean;
  /**
   * Forwarded to the payment flows (AddPaymentForm / VoidPaymentDialog): fires on the verbatim
   * "Cashier's shift is not open" backend error so the host can open its shift dialog.
   */
  onCashierShiftRequired?: () => void;
  /**
   * Patient financial stat cards (Total Unpaid / Available Balance) rendered
   * atop the body. Supplied by the appointment Billing tab from the
   * PreLoadAppointmentDetails `billingData.availableBalance.balance`.
   * Flutter `PatientAvailableBalanceWidget`.
   */
  availableBalance?: PatientBalanceEntry[];
}

function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function InvoiceDetailPage(props: InvoiceDetailPageProps = {}) {
  const { invoiceId: invoiceIdProp, embedded = false, onCashierShiftRequired, availableBalance } = props;
  const params = useParams<{ invoiceId: string }>();
  // `||`, not `??`: a host that passes `invoiceId=""` before its billingData id has loaded must
  // fall back to the route param, exactly as pm-react does (Samer review PR #129 §3 there).
  const invoiceId = invoiceIdProp || params.invoiceId;
  const navigate = useNavigate();
  const location = useLocation();
  // pm-react: Back goes to the claim workbench when there is no in-app entry. The PA has no
  // workbench (and no routed invoice page); embedded mode never shows Back.
  const goBack = () => navigate(-1);
  const user = useAuthStore((s) => s.user);
  const currency = user?.organizationCurrency ?? 'AED';

  const { data, isLoading, isError } = useInvoice({ invoiceId });
  const paymentsQuery = usePaymentsForInvoice(invoiceId);
  const payments = paymentsQuery.data ?? [];
  const printInvoice = usePrintInvoice();
  const printReceipt = usePrintReceipt();

  /**
   * Draft-invoice note (pm-react PROD-1760). `null` means "untouched", so the issue call can tell
   * "user cleared the note" (`''`) from "never typed" (`null`) and re-send the stored value.
   */
  const [noteDraft, setNoteDraft] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [issueDate, setIssueDate] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [mgmtDiscountOpen, setMgmtDiscountOpen] = useState(false);
  const [discountTarget, setDiscountTarget] = useState<InvoiceService | null>(null);
  const [voidTarget, setVoidTarget] = useState<PaymentRow | null>(null);
  // The Draft edit handlers (one PUT /Invoice/{id} each). A hook, so it sits above the early returns. pm-react's
  // `useCancellationReasons(cancelOpen)` is the Cancel dialog's own (`InvoiceCancelDialog`), which keeps the React #310
  // hook-order rule by owning its hook list.
  const lineEdits = useInvoiceLineEdits(invoiceId);
  // Order to Invoice (pm-react PROD-2592): lines added from orders. Above the early returns for the
  // same hook-order reason.
  const orderLineState = useInvoiceOrderLines(invoiceId);

  if (isLoading) return <LoadingDetail />;
  if (isError || !data) return <ErrorState onBack={embedded ? undefined : goBack} />;

  const invoice = data;
  const isCancelled = invoice.invoiceStatus === 'Cancelled';
  // Flutter invoice action gating (`invoice_billing_screen.dart:380-479`):
  //  - Draft → only "Issue Invoice" (no Cancel/Print/Receive).
  //  - Issued → Preview (Print); Receive Payment if outstanding; Cancel only
  //    when there's no claim yet OR the claim is still Initial/Hold.
  const isDraft = invoice.invoiceStatus === 'Draft';
  // Inline add-payment shows while the patient still owes on a live invoice.
  const canReceivePayment = !isCancelled && !isDraft && (invoice.patientOutstanding ?? 0) > 0;
  // Numeric available balance for the Balance tile — the "positive" entry of the balance cards.
  const availableBalanceAmount = (availableBalance ?? []).find(
    (b) => (b.financialEventStatus ?? '').toLowerCase() === 'positive',
  )?.amount;
  const issueBlockedByOrders = isDraft && orderLineState.blocking.length > 0;
  const claimStatus = invoice.claim?.claimStatus;
  const cancelAllowed = !invoice.claim || claimStatus === 'Initial' || claimStatus === 'Hold';
  // Self-pay invoices never go to a claim, so the claim-readiness validation is noise — hidden
  // (pm-react, Andrew review 2026-06-04). Insurance/Corporate keep the banner.
  const isSelfPay = isSelfPayType(invoice.sponsorType);
  // Issue-date defaults to the ENCOUNTER date (Flutter `InvoiceDateSelectionPopUp`), else today.
  const encIso = invoice.encounterDate ?? '';
  const encDatePart = encIso.length >= 10 ? encIso.slice(0, 10) : todayYmd();
  const encTimePart = (() => {
    const t = encIso.includes('T') ? encIso.slice(11).replace(/Z$/, '') : '';
    if (!t) return '00:00:00.000';
    return t.includes('.') ? t : `${t}.000`;
  })();
  const hasNoServices = (invoice.services ?? []).length === 0;

  if (!invoiceId) return <ErrorState onBack={embedded ? undefined : goBack} />;

  // Opens the Issue dialog seeded with the encounter date.
  const onIssueClick = (seedDate: string) => {
    setIssueDate(seedDate);
    setIssueOpen(true);
  };
  const onCancelClick = () => setCancelOpen(true);
  const onAddService = () => setPickerOpen(true);
  const onEditDiscount = (service: InvoiceService) => setDiscountTarget(service);
  const onManagementDiscountClick = () => setMgmtDiscountOpen(true);

  return (
    <div data-slot="billing-body" className="flex flex-col h-full overflow-hidden">
      {/* pm-react names the invoice in the top bar on its routed page only (`InvoiceBarCrumb`); embedded, the host
          owns the bar. The PA has no routed invoice page, so there is no crumb. */}
      <PageHeader
        inset="page"
        title={invoice.invoiceNumber ?? 'Invoice'}
        back={
          embedded ? undefined : (
            <Button variant="ghost" size="icon-sm" aria-label="Back" onClick={goBack}>
              <ChevronLeft className="size-4" aria-hidden />
            </Button>
          )
        }
        meta={
          <span className="flex items-center gap-2">
            {invoice.invoiceStatus && (
              <BridgeStatusChip semantic={STATUS_BADGE_SEMANTIC[invoice.invoiceStatus] ?? 'neutral'} tone="soft">
                {invoice.invoiceStatus}
              </BridgeStatusChip>
            )}
            {invoice.patientPaymentStatus && (
              <BridgeStatusChip
                semantic={PAYMENT_BADGE_SEMANTIC[invoice.patientPaymentStatus] ?? 'neutral'}
                tone="soft"
              >
                {invoice.patientPaymentStatus}
              </BridgeStatusChip>
            )}
          </span>
        }
        actions={
          <>
            {/* Draft → Issue Invoice (Flutter: enabled only with ≥1 service). */}
            {!isCancelled && isDraft && (
              <div className="flex flex-col items-end gap-1">
                <Button onClick={() => onIssueClick(encDatePart)} disabled={hasNoServices || issueBlockedByOrders}>
                  Issue Invoice
                </Button>
                {(hasNoServices || issueBlockedByOrders) && (
                  <span className="max-w-60 text-right text-xs leading-4 text-text-muted">
                    {issueBlockedByOrders ? ISSUE_BLOCKED_HINT : 'Add at least one service to issue'}
                  </span>
                )}
              </div>
            )}
            {/* Print / Preview is hidden on Draft and Cancelled invoices (Flutter: Preview only when
                invoiceStatus != Draft; the backend PDF may be stale/empty for a cancelled invoice). */}
            {!isCancelled && !isDraft && (
              <Button
                variant="ghost"
                onClick={() => invoiceId && printInvoice.mutate(invoiceId)}
                disabled={!invoiceId || printInvoice.isPending}
              >
                {printInvoice.isPending ? 'Opening…' : 'Print Invoice'}
              </Button>
            )}
            {/* Cancel only on a non-Draft invoice whose claim hasn't progressed past Initial/Hold. */}
            {!isCancelled && !isDraft && cancelAllowed && (
              <Button variant="ghost" className="text-status-error hover:bg-red-100" onClick={onCancelClick}>
                Cancel Invoice
              </Button>
            )}
          </>
        }
      />

      {/* Body */}
      <div className="flex-1 min-h-0 overflow-auto scrollbar-hide px-12 pt-8 pb-12 space-y-6">
        {/* Patient financial stat cards — Total Unpaid + Available Balance (appointment Billing tab only). */}
        {availableBalance && availableBalance.length > 0 && (
          <PatientBalanceCards
            balance={availableBalance}
            currency={currency}
            // Available-Balance link → the patient's advance-receipts view (Flutter `billing_tab.dart:316`).
            // `from` so the patient page's Back returns to this appointment.
            onAvailableClick={() => {
              const pid = invoice.patientId ?? invoice.patient?.id;
              if (!pid) return;
              navigate(`/patients/edit/${pid}?tab=billing&billingView=receipts`, {
                state: { from: `${location.pathname}${location.search}` },
              });
            }}
          />
        )}

        {/* Cancelled banner */}
        {isCancelled && (
          <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-100 px-4 py-3 text-sm leading-5 text-red-700">
            <AlertCircle className="w-4 h-4 mt-1 shrink-0" aria-hidden />
            <div>
              <div className="font-semibold">Invoice cancelled</div>
              {invoice.cancelledReason && <div className="mt-1">Reason: {invoice.cancelledReason}</div>}
            </div>
          </div>
        )}

        {/* pm-react PROD-2592: an order was removed from the plan after its line was added. Keep or Drop is on the
            line; Issue waits for it. */}
        {issueBlockedByOrders && (
          <div className="flex items-start gap-2 rounded-xl border border-yellow-200 bg-yellow-100 px-4 py-3 text-sm leading-5 text-yellow-700">
            <AlertCircle className="w-4 h-4 mt-1 shrink-0" aria-hidden />
            <div>
              <div className="font-semibold">An order on this invoice was removed from the plan</div>
              <div className="mt-1">
                {orderLineState.blocking.map((l) => l.display).join(', ')} left Assessment &amp; Plan after it was
                added. Keep the line or drop it before you issue. Dropping removes the invoice line only; it never
                changes the order.
              </div>
            </div>
          </div>
        )}

        {/* Errors */}
        {!isSelfPay && (invoice.errors ?? []).length > 0 && (
          <div className="rounded-xl border border-yellow-200 bg-yellow-100 px-4 py-3 text-sm leading-5 text-yellow-700">
            <div className="font-semibold mb-1">Validation</div>
            <ul className="list-disc pl-6 space-y-1">
              {(invoice.errors ?? []).map((err, i) => (
                <li key={i}>
                  {err.field ? `${err.field}: ` : ''}
                  {err.message ?? err.code ?? 'Unknown error'}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* INVOICE — claim-style body: the Review Claim panel blocks in the `invoice` variant (info grid + diagnosis
            + services + note/totals), matching Flutter invoice_billing_screen. pm-react sizes this heading with a
            type token it never defines (`--type-section-size`), so it inherits the body size there; same here. */}
        <h2 className="font-semibold text-text-primary">Invoice</h2>

        <ClaimReviewInfoGrid
          invoice={invoice}
          variant="invoice"
          // Embedded (the appointment Billing tab) keeps the patient icon decorative: navigating away would tear
          // down the host panel. The PA has no routed invoice page, so this is always embedded.
          onViewPatient={
            embedded
              ? undefined
              : () => {
                  const pid = invoice.patientId ?? invoice.patient?.id;
                  if (!pid) return;
                  navigate(`/patients/edit/${pid}`, {
                    state: { from: `${location.pathname}${location.search}` },
                  });
                }
          }
        />

        <ClaimReviewDiagnosisSection
          invoice={invoice}
          editable={false}
          invoiceId={invoiceId}
          updateType=""
          emptyText="No diagnosis available"
        />

        <InvoiceServicesTable
          services={invoice.services ?? []}
          sponsorType={invoice.sponsorType}
          currency={currency}
          // Per-service discount editing is Draft-only (pm-react PROD-1759).
          onEditDiscount={isDraft ? onEditDiscount : undefined}
          editable={isDraft}
          onAddService={onAddService}
          onRemoveService={lineEdits.removeService}
          onChangeQty={lineEdits.changeQty}
          onChangeCoverage={lineEdits.changeCoverage}
          onChangeServiceDate={lineEdits.changeServiceDate}
          onChangeAuthCode={lineEdits.changeAuthCode}
          onChangeValidity={lineEdits.changeValidity}
          orderLines={orderLineState.orderLines}
          onRevertOrderLine={isDraft ? orderLineState.revert : undefined}
          onKeepOrderLine={orderLineState.keep}
          busy={lineEdits.pending || orderLineState.busy}
        />

        <InvoicePendingOrders invoiceId={invoiceId} isDraft={isDraft} currency={currency} />

        {/* Note (left) + totals box (right) — Flutter invoice_billing_screen. */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start">
          <section
            data-billing="note"
            className="flex min-w-0 flex-col rounded-xl border border-border-default bg-surface-card px-6 py-6 md:flex-1"
          >
            {/* Enable condition is Flutter's verbatim `invoice?.invoiceNumber == null`, NOT `isDraft` (pm-react
                PROD-1760). `maxLength` 300 is `FieldAndLabel`'s default. */}
            <TextField
              label="Note"
              size="compact"
              placeholder="Enter note"
              maxLength={300}
              value={noteDraft ?? invoice.note ?? ''}
              disabled={invoice.invoiceNumber != null}
              onChange={(e) => setNoteDraft(e.target.value)}
            />
          </section>
          <div className="flex flex-col gap-3 md:w-80 md:shrink-0">
            <ClaimReviewTotalsBox
              invoice={invoice}
              currency={currency}
              variant="invoice"
              // Self-pay + Draft: the management-discount dialog is reachable from the Patient Payable row (or the
              // Management Discount row once one exists). React gates on `isDraft` (pm-react PROD-1794 §B).
              onPatientPayableClick={isSelfPay && isDraft ? onManagementDiscountClick : undefined}
            />
            {isDraft && <InvoicePendingOrdersTotal invoiceId={invoiceId} currency={currency} />}
          </div>
        </div>

        {/* "Paid by package" (pm-react PROD-2126) — its own section ABOVE Payments; renders nothing without a
            standing redemption, and nothing on a draft. */}
        <PackageRedemptionsNote invoiceId={invoiceId} enabled={!isDraft} currency={currency} />

        {/* Payments — read-only receipt cards (Print + Void on an actionable row). */}
        {(payments.length > 0 || canReceivePayment) && (
          <section className="space-y-3">
            <h3 className="font-semibold text-text-primary">Payments</h3>
            {payments.map((p) => (
              <PaymentReceiptCard
                key={p.id}
                payment={p}
                currency={currency}
                actions={
                  <InvoiceReceiptActions
                    payment={p}
                    invoiceId={invoiceId}
                    printPending={printReceipt.isPending}
                    onPrint={(args) => printReceipt.mutate(args)}
                    onVoid={setVoidTarget}
                  />
                }
              />
            ))}
            {/* Inline add-payment while the patient still owes on a live invoice. */}
            {canReceivePayment && invoiceId && (
              <AddPaymentForm
                invoiceId={invoiceId}
                patientPayable={invoice.patientPayable ?? 0}
                patientOutstanding={invoice.patientOutstanding ?? undefined}
                availableBalance={availableBalanceAmount}
                onCashierShiftRequired={onCashierShiftRequired}
              />
            )}
          </section>
        )}
      </div>

      <InvoiceCancelDialog open={cancelOpen} onOpenChange={setCancelOpen} invoiceId={invoiceId} />

      {/* Issue dialog — Flutter InvoiceDateSelectionPopUp: the invoice date is the picked date at the encounter's time
          of day, within [encounter date .. today]. */}
      <InvoiceIssueDialog
        open={issueOpen}
        onOpenChange={setIssueOpen}
        invoiceId={invoiceId}
        issueDate={issueDate}
        onIssueDateChange={setIssueDate}
        minDate={encDatePart}
        maxDate={todayYmd()}
        encTimePart={encTimePart}
        noteDraft={noteDraft}
        storedNote={invoice.note}
      />

      {/* Add-service picker (Draft) */}
      <ServicePickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        providerId={invoice.providerId ?? invoice.provider?.id}
        locationId={invoice.locationId}
        patientId={invoice.patientId ?? invoice.patient?.id}
        currency={currency}
        existingServiceIds={(invoice.services ?? [])
          .map((s) => s.serviceId)
          .filter((id): id is string => Boolean(id))}
        onSelect={lineEdits.addService}
      />

      {/* Management discount (Self-pay), opened from Patient Payable / the Management Discount row. */}
      <ManagementDiscountDialog
        open={mgmtDiscountOpen}
        onOpenChange={setMgmtDiscountOpen}
        invoiceId={invoiceId}
        currency={currency}
        patientPayable={invoice.patientPayable ?? undefined}
      />

      {/* Line discount: mounted per target, so each open seeds from that service. */}
      {discountTarget && (
        <EditServiceDiscountDialog
          open
          onOpenChange={(o) => !o && setDiscountTarget(null)}
          invoiceId={invoiceId}
          service={discountTarget}
        />
      )}

      {/* Void payment, mounted per target. The print preview is AppShell's (see header). */}
      {voidTarget && (
        <VoidPaymentDialog
          open
          onOpenChange={(o) => !o && setVoidTarget(null)}
          payment={voidTarget}
          invoiceId={invoiceId}
          onCashierShiftRequired={onCashierShiftRequired}
        />
      )}
    </div>
  );
}

function LoadingDetail() {
  return (
    <div className="flex flex-col h-full overflow-hidden p-8 space-y-4" aria-busy="true">
      <Skeleton className="h-8 w-72" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-48 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

interface ErrorStateProps {
  /** Undefined in embedded mode — the host owns back navigation. */
  onBack?: () => void;
}

function ErrorState({ onBack }: ErrorStateProps) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 px-8 py-12">
      <AlertCircle className="w-8 h-8 text-text-muted" aria-hidden />
      <div className="text-sm leading-5 text-text-primary font-semibold">Could not load invoice</div>
      <p className="text-sm leading-5 text-text-muted max-w-md text-center">
        The invoice may have been removed or you may not have permission. Go back to the workbench and try again.
      </p>
      {onBack && (
        <Button variant="ghost" onClick={onBack}>
          <X className="w-4 h-4 mr-1" aria-hidden />
          Back
        </Button>
      )}
    </div>
  );
}

export default InvoiceDetailPage;
